import { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const fmtMoney = (n) => Math.round(n).toLocaleString('he-IL');

export default function PortfolioEvolutionChart({ history }) {
  // We need at least 2 points to show a meaningful sequence/evolution
  if (!history || history.length < 2) return null;

  const data = useMemo(() => {
    // History is usually sorted by period ascending (from holdingsHistory.js).
    // Ensure we sort it chronologically just in case.
    const sorted = [...history].sort((a, b) => (a.period < b.period ? -1 : 1));
    return sorted.map(snap => {
      // period is YYYY-MM
      const [yy, mm] = snap.period.split('-');
      const label = `${mm}/${yy.slice(2)}`;
      return {
        label,
        period: snap.period,
        balance: snap.totalBalance || 0,
        deposits: snap.totalDeposits || 0,
        withdrawals: snap.totalWithdrawals || 0,
        ret: snap.ret || 0
      };
    });
  }, [history]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { label, balance, ret } = payload[0].payload;
    return (
      <div style={{
        background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: '10px 14px', fontSize: 13, direction: 'rtl', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: '#475569' }}>תקופה: {label}</div>
        <div style={{ color: '#0f172a', display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span>שווי התיק:</span>
          <span style={{ fontWeight: 700 }}>{fmtMoney(balance)} ₪</span>
        </div>
        {ret != null && (
          <div style={{ color: ret >= 0 ? '#16a34a' : '#dc2626', display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 4 }}>
            <span>תשואה תקופתית:</span>
            <span style={{ fontWeight: 700, direction: 'ltr' }}>{ret > 0 ? '+' : ''}{(ret * 100).toFixed(2)}%</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      background: 'white', borderRadius: 16, padding: '20px 16px 16px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9',
    }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 2, textAlign: 'center' }}>
        התפתחות התיק
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12, textAlign: 'center' }}>
        צמיחת שווי התיק הכולל (בש״ח)
      </div>
      
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4338ca" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#4338ca" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis 
            dataKey="label" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#64748b', fontSize: 11 }} 
            dy={10} 
          />
          <YAxis 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#64748b', fontSize: 11 }} 
            tickFormatter={(val) => val >= 1000000 ? (val / 1000000).toFixed(1) + 'M' : val >= 1000 ? (val / 1000).toFixed(0) + 'K' : val}
            dx={-10}
            orientation="left"
          />
          <Tooltip content={<CustomTooltip />} />
          <Area 
            type="monotone" 
            dataKey="balance" 
            stroke="#4338ca" 
            strokeWidth={3}
            fillOpacity={1} 
            fill="url(#colorBalance)" 
            animationDuration={1500}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
